import styled, { css } from "styled-components";
export {
	SectionAllButton as AllButton,
	SliderArrowButton as ArrowButton,
	SliderArrowIcon as ArrowIcon,
	SliderControls as Controls,
	SectionTitle as TitleBox,
} from "../shared/sliderPrimitives";

const glassCardBorder = css`
	isolation: isolate;

	&::after {
		content: "";
		position: absolute;
		inset: 0;
		border-radius: inherit;
		border: 1px solid #ffffff;
		mix-blend-mode: overlay;
		pointer-events: none;
		z-index: 2;
	}
`;

export const WRAPPER = styled.section`
	width: 100%;
	margin-bottom: 24px;

	@media (max-width: 767px) {
		margin-bottom: 14px;
	}
`;

export const HEADER = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
`;

export const Viewport = styled.div`
	overflow: hidden;
	width: 100%;
	padding-top: ${({ $rowAspectRatio = "4 / 5" }) =>
		$rowAspectRatio === "1 / 1" ? "12px" : "16px"};
	padding-bottom: 0;
	user-select: none;
	-webkit-user-select: none;
`;

export const Track = styled.div`
	display: flex;
	width: 100%;
	gap: ${({ $gap }) => `${$gap}px`};
	align-items: flex-start;
	transform: translateX(
		calc(
			-1 * ${({ $index }) => $index} * (100% + ${({ $gap }) => `${$gap}px`}) / ${({ $count }) => $count}
		)
	);
	transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
	will-change: transform;
`;

export const CardImage = styled.img`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
	object-position: center center;
	display: block;
	transition:
		filter 0.2s ease,
		opacity 0.2s ease;
	user-drag: none;
	-webkit-user-drag: none;
	user-select: none;
	-webkit-user-select: none;
`;

export const CardHoverOverlay = styled.div`
	position: absolute;
	inset: 0;
	z-index: 1;
	background: linear-gradient(0deg, rgba(1, 1, 3, 0.8) 0%, rgba(1, 1, 3, 0.8) 100%);
	opacity: 0;
	pointer-events: none;
	cursor: pointer;
	border-radius: inherit;
	transition: opacity 0.2s ease;
`;

export const CardGameName = styled.span`
	position: absolute;
	top: 14px;
	left: 4px;
	right: 4px;
	text-align: center;
	color: #ffffff;
	font-size: 12px;
	font-weight: 700;
	font-family: var(--bt-font-family);
	text-shadow: 0 1px 6px rgba(0, 0, 0, 0.9);
	display: -webkit-box;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
	overflow: hidden;
	overflow-wrap: break-word;
	z-index: 2;

	@media (min-width: 768px) {
		font-size: 13px;
	}

	@media (min-width: 1024px) {
		font-size: 14px;
	}
`;

export const CardPlayIcon = styled.img`
	position: absolute;
	left: 50%;
	top: calc(50% + 10px);
	transform: translate(-50%, -50%);
	width: 40px;
	height: 40px;
	filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.5));
	z-index: 2;
`;

export const Card = styled.div`
	flex: 0 0
		calc(
			(100% - ${({ $count, $gap }) => `${($count - 1) * $gap}px`}) / ${({ $count }) => $count}
		);
	align-self: flex-start;
	height: auto;
	aspect-ratio: ${({ $aspectRatio = "4 / 5" }) => $aspectRatio};
	border-radius: 12px;
	box-sizing: border-box;
	overflow: hidden;
	cursor: pointer;
	position: relative;
	z-index: 0;
	background: #171c2a;
	transition:
		transform 0.2s ease-in-out,
		background 0.2s ease;
	${glassCardBorder}

	& > img,
	a ${CardImage} {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center center;
	}

	a {
		position: absolute;
		inset: 0;
		display: block;

		${CardImage} {
			position: absolute;
			inset: 0;
			width: 100%;
			height: 100%;
		}
	}

	&:hover {
		transform: translateY(-12px);
		z-index: 2;
	}

	&:hover ${CardHoverOverlay} {
		opacity: 1;
		pointer-events: auto;
	}

	&:hover ${CardPlayIcon} {
		transform: translate(-50%, -50%);
	}
`;

export const ViewAllIcon = styled.img`
	position: relative;
	z-index: 1;
	width: 30px;
	height: 30px;
	display: block;
	flex-shrink: 0;
	transition: filter 0.2s ease;
`;

export const ViewAllLabel = styled.span`
	position: relative;
	z-index: 1;
	max-width: 85%;
	font-size: 14px;
	font-weight: 600;
	color: #e5f0fe;
	text-align: center;
	font-family: var(--bt-font-family);
`;

export const ViewAllCard = styled.div`
	flex: 0 0
		calc(
			(100% - ${({ $count, $gap }) => `${($count - 1) * $gap}px`}) / ${({ $count }) => $count}
		);
	align-self: flex-start;
	height: auto;
	aspect-ratio: ${({ $aspectRatio = "4 / 5" }) => $aspectRatio};
	border-radius: 12px;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	text-align: center;
	padding: 12px 8px;
	background: #171c2a;
	cursor: pointer;
	position: relative;
	z-index: 0;
	transition:
		transform 0.2s ease-in-out,
		background 0.2s ease;
	${glassCardBorder}

	&::before {
		content: "";
		position: absolute;
		inset: 0;
		border-radius: inherit;
		background: radial-gradient(
				ellipse 90% 70% at 0% 0%,
				rgba(82, 151, 255, 0.42) 0%,
				transparent 58%
			),
			radial-gradient(
				ellipse 85% 65% at 100% 0%,
				rgba(82, 151, 255, 0.32) 0%,
				transparent 55%
			),
			radial-gradient(
				ellipse 70% 55% at 100% 100%,
				rgba(82, 151, 255, 0.18) 0%,
				transparent 50%
			);
		pointer-events: none;
		z-index: 0;
	}

	&:hover {
		transform: translateY(-12px);
		z-index: 2;
	}

	&:hover ${ViewAllIcon} {
		filter: brightness(1.25) drop-shadow(0 0 4px rgba(229, 240, 254, 0.55));
	}
`;
